import Image from "next/image";
import { Apple, Google, logoDark } from "../../public/assets";
import snipperFirstFrame from "../../public/assets/images/snippetFirstFrame.jpg";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { BaseStepProps } from ".";
import PhoneInput from "react-phone-input-2";
import { useFormContext } from "react-hook-form";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import dynamic from "next/dynamic";
const Lottie = dynamic(() => import("lottie-react-web"), { ssr: false });
import circularProgress from "../../public/assets/lottie/circularProgress.json";
import useAppleDevice from "../../hooks/isAppleDevice";
import {
  getUserInfoById,
  uploadUserInfo,
  useBoardingFlow,
} from "../../hooks/useBoardingFlow";
import { BoardingFlowContext } from "../../context/auth/BoardingFlowContext";
import { socialMethod } from "./IWantToConnectMyStep";

// const signInWithPhone = ({ phoneNumber, tempData }: SignInWithPhoneProps) => {};

interface LandingStepProps extends BaseStepProps {}
function LandingStep(props: LandingStepProps, ref: any) {
  const [canPlay, setCanPlay] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const isAppleDevice = useAppleDevice();
  const [socialProcessStarted, setSocialProcessStarted] =
    useState<socialMethod | null>(null);
  const [phoneRoute, setPhoneRoute] =
    useContext(BoardingFlowContext).phoneRoute;
  const [, setActiveTab] = useContext(BoardingFlowContext).activeTab;
  const allowCloseRef = useContext(BoardingFlowContext).allowCloseRef;

  const { getValues, setValue, formState, setError, clearErrors } =
    useFormContext();

  const phoneNumberValue = getValues("phoneNumber");
  const {
    signInWithPhone,
    onSignInWithAppleBtnClick,
    onSignInWithGoogleBtnClick,
  } = useBoardingFlow();

  useImperativeHandle(ref, () => ({
    reset: () => {
      setValue("phoneNumber", "+91");
      setValue("countryCode", "+91");
      setSendingOtp(false);
      setPhoneRoute(false);
      setSocialProcessStarted(null);
      clearErrors();
    },
  }));

  const handleSignInWithPhone = async () => {
    console.group("Login Flow Started | Phone - ");
    if (sendingOtp) return;
    setSendingOtp(true);
    allowCloseRef.current = false;

    try {
      console.log("Signing out before proceeding to login flow");
      await require("../../firebase-config").auth.signOut();
      await require("../../firebase-config").messaging.deleteToken();
    } catch (e) {}

    setPhoneRoute(true);
    clearErrors();
    signInWithPhone({
      phoneNumber: phoneNumberValue,
      setIsPending: setSendingOtp,
      handleSuccess: () => {
        console.log("OTP sent successfully, Now setting the active tab to ", 1);
        setPhoneRoute(true);
        setSendingOtp(false);
        setActiveTab(1);
      },
      handleError: (errorMessage) => {
        console.log("Error in sending OTP - ", errorMessage);
        setSendingOtp(false);
        setError(
          "phoneNumber",
          {
            message:
              errorMessage ?? "Something went wrong. Please try again later.",
          },
          { shouldFocus: true }
        );
      },
    });
  };

  const handleSignInWithExistingPhone = async () => {
    console.warn("Sign in with existing phone started - ");
    signInWithPhone({
      phoneNumber: getValues("phoneNumber"),
      setIsPending: () => {},
      handleSuccess: () => {
        console.log("OTP sent successfully, Now setting the active tab to ", 2);
        setSocialProcessStarted(null);
        setActiveTab(2);
      },
      handleError: (errorMessage) => {
        console.log("Error in sending OTP - ", errorMessage);
        setSocialProcessStarted(null);
        setError(
          "email",
          {
            message:
              errorMessage ?? "Something went wrong. Please try again later.",
          },
          { shouldFocus: true }
        );
      },
    });
  };

  const handleSignUpWithEmail = async (method: socialMethod) => {
    console.groupEnd();
    console.group("Login Flow Started | Email - ", method);
    try {
      if (socialProcessStarted) {
        throw new Error("One Process has already started");
      }
      setSocialProcessStarted(method);
      console.log("Signing out before proceeding to login flow");
      await require("../../firebase-config").auth.signOut();
      await require("../../firebase-config").messaging.deleteToken();
      allowCloseRef.current = false;
      // setPhoneRoute(false);
      setValue("phoneNumber", "+91");
      setValue("countryCode", "+91");
      let user: firebase.User | undefined = undefined;
      if (method === "google") {
        user = await onSignInWithGoogleBtnClick();
      }
      if (method === "apple") {
        user = await onSignInWithAppleBtnClick();
      }
      console.log("Signed in user - ", user);
      if (user?.email) {
        let userData = await getUserInfoById(user?.uid);
        if (!userData.email || !userData.phone_number) {
          userData = await uploadUserInfo({
            userId: user?.uid,
            avatarUrl: user?.photoURL ?? null,
            name: user?.displayName ?? null,
            email: user?.email ?? null,
            phoneNumber: userData.phone_number ?? null,
            countryCode: userData.phone_country_code ?? null,
            isInstructor: false,
          });
        }

        if (userData.phone_number) {
          setValue(
            "phoneNumber",
            "+" + userData.phone_country_code + userData.phone_number
          );
          setError("phoneExists", {
            message: "Account already exists.",
          });
          return handleSignInWithExistingPhone();
        }

        setSocialProcessStarted(null);
        return setActiveTab(1);
      }
    } catch (e) {
      console.error("Error in signing in with email - ", e);
      setSocialProcessStarted(null);
      setError("email", {
        message: e.message ?? "Something went wrong. Please try again later.",
      });
      setTimeout(() => clearErrors(), 4000);
    }
  };

  useEffect(() => {
    const wndw = window as any;

    if (!wndw.recaptchaVerifier && props.isOpen) {
      wndw.recaptchaVerifier =
        new (require("../../firebase-config").firebase.auth.RecaptchaVerifier)(
          `recaptcha-container-2`,
          {
            size: "invisible",
            callback: (response) => {
              console.log({ response });
            },
            "expired-callback": () =>
              wndw.recaptchaVerifier && wndw.recaptchaVerifier.clear(),
          },
          require("../../firebase-config").firebaseApp
        );
      return () => {
        wndw.recaptchaVerifier = null;
      };
    }
    if (!props.isOpen) wndw.recaptchaVerifier = null;
  }, [props.isOpen]);

  return (
    <div className="slide__page">
      <div className="first__page__inner">
        <div className="top__area" />
        <div className="plan__success">
          <Image height={100} width={100} src={logoDark} alt="logo" />
          <h6>
            Learning made simple. <span>Aasaan hai!</span>
          </h6>
        </div>
        <div className="snippet__video">
          <Image
            height={100}
            width={100}
            style={{ width: "100%", position: "absolute", display: "block" }}
            src={snipperFirstFrame}
            alt=""
          />
          <video
            onCanPlay={() => {
              setCanPlay(true);
            }}
            style={{
              width: "100%",
              position: "relative",
              zIndex: 2,
              display: canPlay ? "block" : "none",
            }}
            src="https://d1kjns6e6wnqfd.cloudfront.net/snippet2.mp4"
            autoPlay={props.isOpenAndActive}
            loop={props.isOpenAndActive}
            playsInline
            muted
            key={"isOpen"}
          />
        </div>
        <div className="registration__page">
          <div className="phone__number">
            {props.isOpen && (
              <div>
                <div id="recaptcha-container-2" className="recaptcha" />
              </div>
            )}

            <div
              onMouseLeave={() => {
                // !localStorage.getItem("closeInstallApp") &&
                //   setCloseInstallApp(false);
                // setPushDown(false);
              }}
            >
              <PhoneInput
                country="in"
                value={phoneRoute ? phoneNumberValue : "+91"}
                isValid={(_, country) => {
                  // @ts-ignore
                  setValue("countryCode", country.dialCode);
                  return true;
                }}
                onChange={(phone) => {
                  //   setPhoneNumber("+" + phone);
                  //   setPhoneNumberError("");
                  setValue("phoneNumber", "+" + phone);
                  clearErrors("phoneNumber");
                }}
                placeholder="+91 XXXXX-XXXXX"
                preferredCountries={["us", "ae", "sg", "my", "in"]}
                inputProps={{ autoFocus: true }}
                onClick={() => {
                  //   setCloseInstallApp(true);
                  // setPushDown(true);
                }}
                onEnterKeyPress={handleSignInWithPhone}
              />

              {formState.errors.phoneNumber && (
                <div className="phone__error__message">
                  <ErrorOutlineIcon />
                  {formState.errors.phoneNumber.message as string}
                </div>
              )}
            </div>
            <button
              id="sign-in-with-phone"
              onClick={async () => {
                handleSignInWithPhone();
              }}
            >
              {sendingOtp ? (
                <Lottie
                  options={{ animationData: circularProgress, loop: true }}
                />
              ) : (
                "Send OTP"
              )}
            </button>
          </div>
          {/* style={{ display: pushDown && "none" }} */}
          <div className="or__line">
            <h6></h6>
            <h5>OR</h5>
            <h6></h6>
          </div>
          {isAppleDevice && (
            <div className="signin__apple">
              <button
                onClick={async () => {
                  handleSignUpWithEmail("apple");
                }}
              >
                <Image
                  height={100}
                  width={100}
                  src={Apple}
                  draggable={false}
                  alt="apple icon"
                />
                {socialProcessStarted === "apple" ? (
                  <Lottie
                    options={{ animationData: circularProgress, loop: true }}
                  />
                ) : (
                  <h6>Continue with Apple</h6>
                )}
              </button>
            </div>
          )}
          <div
            className="social__buttons"
            // style={{ display: pushDown && "none" }}
          >
            <button
              className="google"
              onClick={async () => {
                handleSignUpWithEmail("google");
              }}
            >
              <Image
                height={100}
                width={100}
                src={Google}
                draggable={false}
                alt="google icon"
              />
              {socialProcessStarted === "google" ? (
                <Lottie
                  options={{ animationData: circularProgress, loop: true }}
                />
              ) : (
                <h6>Continue with Google</h6>
              )}
            </button>
          </div>
          {/* style={{ display: pushDown && "none" }} */}
          <div className="agreements">
            <h6>Having trouble?</h6>
            <div>
              <h5>
                Reach us at{" "}
                <a
                  href="mailto:help@pustack.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  help@pustack.com
                </a>
              </h5>
            </div>
          </div>
        </div>

        {formState.errors.email && (
          <div className="error__message fadeIn">
            {formState.errors.email.message as string}
          </div>
        )}
      </div>
    </div>
  );
}

export default forwardRef(LandingStep);
